import { AntTag, Text } from '@agro-club/agroclub-shared'
import useFormatDate from 'hooks/useFormatDate'
import { DASH } from 'modules/constants'
import { refetchFunc } from 'modules/domain/common/hooks'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { SampleRequestDocsType, SampleRequestType, SamplesProgressStatuses } from 'views/pages/Samples/types'
import { SamplesCommonTable } from '../SamplesCommonTable'
import { RowGapWrapper } from '../styled'
import { AddSampleRequestDocs } from './AddSampleRequestDocs'
import { SampleDocsByRegional } from './SampleDocsByRegional'

type Props = {
  sampleRequest: SampleRequestType
  sampleRequestRefetch: refetchFunc
}

export const SuccessFlowStatusses = [
  [SamplesProgressStatuses.ASSIGNED_TO_REGIONAL, SamplesProgressStatuses.ASSIGNED_TO_AGENT],
  SamplesProgressStatuses.ACCEPTED_BY_REGIONAL,
  SamplesProgressStatuses.REGIONAL_SENT_FILES,
  SamplesProgressStatuses.REGIONAL_SENT_SAMPLES,
  SamplesProgressStatuses.ACCEPTED_BY_LAB,
  SamplesProgressStatuses.COMPLETED,
]

const getStatusIndex = (status: SamplesProgressStatuses | SamplesProgressStatuses[]) => {
  if (Array.isArray(status)) {
    return 0
  }
  if (SuccessFlowStatusses[0].includes(status)) {
    return 0
  } else {
    return SuccessFlowStatusses.indexOf(status)
  }
}
export const History: FC<Props> = ({ sampleRequest, sampleRequestRefetch }) => {
  const { t } = useTranslation(['deal', 'samples'])
  const formateDate = useFormatDate()

  const regionalDocs = sampleRequest.documents.filter(
    (doc) => doc.document_type === SampleRequestDocsType.regional_files,
  )
  const exportDocs = sampleRequest.documents.filter((doc) => doc.document_type === SampleRequestDocsType.export_files)
  const localDocs = sampleRequest.documents.filter((doc) => doc.document_type === SampleRequestDocsType.local_files)
  const deliveryDocs = sampleRequest.documents.filter(
    (doc) => doc.document_type === SampleRequestDocsType.delivery_docs,
  )

  const historyOpt = () => {
    const historyForRender = sampleRequest.updates.filter((el) => el.status !== SamplesProgressStatuses.CREATED)
    const lastUpdate = historyForRender[historyForRender.length - 1]
    const latestIndex = getStatusIndex(lastUpdate.status)
    // check if all prev success statuses are present
    // and insert missed
    if (latestIndex > 0) {
      const itemsToInsert: SampleRequestType['updates'] = []
      for (let i = 0; i < latestIndex; i++) {
        let hasInRender = false
        const successStatus = SuccessFlowStatusses[i]
        if (Array.isArray(successStatus)) {
          hasInRender =
            !!historyForRender.find((el) => el.status === successStatus[0]) ||
            !!historyForRender.find((el) => el.status === successStatus[1])
        } else {
          hasInRender = !!historyForRender.find((el) => el.status === successStatus)
        }

        if (!hasInRender) {
          itemsToInsert.push({
            status: Array.isArray(successStatus) ? successStatus[0] : successStatus,
            status_comment: '',
            status_delay_comment: '',
            created: '',
          })
        }
      }
      historyForRender.splice(historyForRender.length - 1, 0, ...itemsToInsert)
    }

    return historyForRender.map((el) => {
      const status = t(`samples:sampleRequestStatuses.${el.status}`)
      const delayComment = el.status_delay_comment ? (
        <div>
          <Text color="secondary" typography="bodyMedium">
            {t('samples:sampleDelay')}:
          </Text>
          {el.status_delay_comment}
        </div>
      ) : undefined
      const label = el.created ? formateDate(el.created, { withTime: true }) : DASH
      if (el.status_comment) {
        return {
          label,
          value: (
            <RowGapWrapper rowGap={10}>
              {el.status_comment}
              {delayComment}
            </RowGapWrapper>
          ),
        }
      } else if (el.status === SamplesProgressStatuses.REGIONAL_SENT_FILES) {
        return {
          label,
          value: (
            <RowGapWrapper rowGap={10}>
              <RowGapWrapper rowGap={4}>
                {status}
                <RowGapWrapper rowGap={10}>
                  <SampleDocsByRegional
                    sampleRequestId={sampleRequest.id}
                    docs={regionalDocs}
                    sampleRequestRefetch={sampleRequestRefetch}
                  />
                  <AddSampleRequestDocs
                    docType={SampleRequestDocsType.regional_files}
                    sampleRequestId={sampleRequest.id}
                    refetchDocs={sampleRequestRefetch}
                  />
                </RowGapWrapper>
              </RowGapWrapper>
              <RowGapWrapper rowGap={4}>
                <Text color="secondary" typography="bodyMedium">
                  {t('samples:forExport')}:
                </Text>
                <RowGapWrapper rowGap={10}>
                  <SampleDocsByRegional
                    sampleRequestId={sampleRequest.id}
                    docs={exportDocs}
                    sampleRequestRefetch={sampleRequestRefetch}
                  />
                  <AddSampleRequestDocs
                    docType={SampleRequestDocsType.export_files}
                    sampleRequestId={sampleRequest.id}
                    refetchDocs={sampleRequestRefetch}
                  />
                </RowGapWrapper>
                <RowGapWrapper rowGap={4}>
                  <Text color="secondary" typography="bodyMedium">
                    {t('samples:forLocal')}:
                  </Text>
                  <RowGapWrapper rowGap={10}>
                    <SampleDocsByRegional
                      sampleRequestId={sampleRequest.id}
                      docs={localDocs}
                      sampleRequestRefetch={sampleRequestRefetch}
                    />
                    <AddSampleRequestDocs
                      docType={SampleRequestDocsType.local_files}
                      sampleRequestId={sampleRequest.id}
                      refetchDocs={sampleRequestRefetch}
                    />
                  </RowGapWrapper>
                </RowGapWrapper>
              </RowGapWrapper>
            </RowGapWrapper>
          ),
        }
      } else if (el.status === SamplesProgressStatuses.REGIONAL_SENT_SAMPLES) {
        const deliveryMethod = sampleRequest.sample_delivery_method
          ? t(`samples:deliveryMethods.${sampleRequest.sample_delivery_method}`)
          : DASH
        return {
          label,
          value: (
            <RowGapWrapper rowGap={10}>
              <RowGapWrapper rowGap={4}>
                {status}
                <SampleDocsByRegional
                  sampleRequestId={sampleRequest.id}
                  docs={deliveryDocs}
                  sampleRequestRefetch={sampleRequestRefetch}
                />
              </RowGapWrapper>
              <AddSampleRequestDocs
                docType={SampleRequestDocsType.delivery_docs}
                sampleRequestId={sampleRequest.id}
                refetchDocs={sampleRequestRefetch}
              />
              <KeyValue label={`${t('samples:deliveryDetails')}:`}>
                {[deliveryMethod, sampleRequest.sample_delivery_comment].filter(Boolean).join('; ')}
              </KeyValue>
              {(sampleRequest.warehouse_location || sampleRequest.warehouse_location_comment) && (
                <KeyValue label={`${t('samples:warehouseInfo')}:`} style={{ wordBreak: 'break-all' }}>
                  <div>{sampleRequest.warehouse_location}</div>
                  <div>{sampleRequest.warehouse_location_comment}</div>
                </KeyValue>
              )}
              {(sampleRequest.scales_location || sampleRequest.scales_location_comment) && (
                <KeyValue label={`${t('samples:scalesInfo')}:`} style={{ wordBreak: 'break-all' }}>
                  <div>{sampleRequest.scales_location}</div>
                  <div>{sampleRequest.scales_location_comment}</div>
                </KeyValue>
              )}
            </RowGapWrapper>
          ),
        }
      } else if (el.status == SamplesProgressStatuses.CANCELED) {
        let extraInfo = ''
        if (sampleRequest.cancel_comment) {
          extraInfo = ` (${sampleRequest.cancel_comment})`
        }
        return { label, value: `${status}${extraInfo}` }
      }

      return {
        label,
        value: (
          <RowGapWrapper rowGap={10}>
            {status} {delayComment}
          </RowGapWrapper>
        ),
      }
    })

    return []
  }

  return (
    <SamplesCommonTable
      header={<AntTag color="green">{t('accordion.sample.updateHistory')}</AntTag>}
      options={historyOpt()}
    />
  )
}
